/**
 * SendLocalListForm — guided table editor for the nested
 * local_authorization_list payload. No JSON box.
 */
import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import type { RemoteCommandCatalogEntry } from "../../../../../api/maintenance";
import "../../../shared/maintenance-tables.scss";
import { useNotice } from "../../../shared/useNotice";

interface Entry {
  id_tag: string;
  status: string;
  expiry_date: string;
  parent_id_tag: string;
}

const STATUSES = ["Accepted", "Blocked", "Expired", "Invalid", "ConcurrentTx"];
const UPDATE_TYPES = ["Full", "Differential"];

const blankEntry = (): Entry => ({
  id_tag: "",
  status: "Accepted",
  expiry_date: "",
  parent_id_tag: "",
});

const toIsoOrUndef = (v: string): string | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

interface Props {
  entry: RemoteCommandCatalogEntry;
  onSubmit: (meta: Record<string, unknown>) => Promise<void> | void;
  submitting: boolean;
  defaults?: Record<string, unknown>;
}

const SendLocalListForm: React.FC<Props> = ({ entry, onSubmit, submitting, defaults }) => {
  const [listVersion, setListVersion] = useState<string>(
    (defaults?.list_version as string | number | undefined)?.toString() ?? "1",
  );
  const [updateType, setUpdateType] = useState<string>(
    (defaults?.update_type as string) ?? "Differential",
  );
  const [entries, setEntries] = useState<Entry[]>([blankEntry()]);
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useNotice("error", null);

  const update = (i: number, patch: Partial<Entry>) =>
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const remove = (i: number) =>
    setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const add = () => setEntries((prev) => [...prev, blankEntry()]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const lv = Number(listVersion);
    if (!Number.isInteger(lv) || lv < 0) {
      setError("list_version must be a non-negative integer");
      return;
    }

    // Strip empty rows (id_tag is required)
    const cleanEntries = entries.filter((e) => e.id_tag.trim());

    // For each non-empty row, validate id_tag
    for (let i = 0; i < cleanEntries.length; i++) {
      if (cleanEntries[i].id_tag.length > 20) {
        setError(`Row ${i + 1}: id_tag must be ≤ 20 chars`);
        return;
      }
    }

    const local_authorization_list = cleanEntries.map((e) => {
      const id_tag_info: Record<string, unknown> = { status: e.status };
      const iso = toIsoOrUndef(e.expiry_date);
      if (iso) id_tag_info.expiry_date = iso;
      if (e.parent_id_tag.trim()) id_tag_info.parent_id_tag = e.parent_id_tag.trim();
      return {
        id_tag: e.id_tag.trim(),
        id_tag_info,
      };
    });

    await onSubmit({
      list_version: lv,
      update_type: updateType,
      local_authorization_list,
    });
  };

  const confirmOk = !entry.destructive || !entry.confirm_text || confirm.trim() === entry.confirm_text;

  return (
    <form className="rcmd-form" onSubmit={handleSubmit}>
      <p className="rcmd-form-description">{entry.description}</p>
      {error && <div className="rcmd-error">{error}</div>}

      <div className="rcmd-form-grid">
        <label className="rcmd-field">
          New list version<span className="rcmd-field-required">*</span>
          <input type="number" min={0} value={listVersion}
            onChange={(e) => setListVersion(e.target.value)} required />
          <span className="rcmd-field-help">Must be greater than device's current version</span>
        </label>
        <label className="rcmd-field">
          Update type<span className="rcmd-field-required">*</span>
          <select value={updateType} onChange={(e) => setUpdateType(e.target.value)}>
            {UPDATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="rcmd-field-help">
            Full = replace whole list (empty rows = clear). Differential = patch.
          </span>
        </label>
      </div>

      <div className="rcmd-section-label" style={{ marginTop: 16 }}>
        Authorization entries
      </div>
      <div className="rcmd-table-editor">
        <table className="maint-stack">
          <thead>
            <tr>
              <th>#</th>
              <th>ID Tag</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Parent tag</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i}>
                <td data-label="#" style={{ width: 32, color: "#999" }}>{i + 1}</td>
                <td data-label="ID Tag"><input value={e.id_tag} maxLength={20}
                  onChange={(ev) => update(i, { id_tag: ev.target.value })} placeholder="ABCD1234" /></td>
                <td data-label="Status">
                  <select value={e.status} onChange={(ev) => update(i, { status: ev.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td data-label="Expires"><input type="datetime-local" value={e.expiry_date}
                  onChange={(ev) => update(i, { expiry_date: ev.target.value })} /></td>
                <td data-label="Parent tag"><input value={e.parent_id_tag} maxLength={20}
                  onChange={(ev) => update(i, { parent_id_tag: ev.target.value })} /></td>
                <td className="rcmd-cell-actions">
                  <button type="button" className="rcmd-table-row-remove" onClick={() => remove(i)}>
                    <X size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="rcmd-table-footer">
          <span>
            {entries.filter((e) => e.id_tag.trim()).length} entries
            {updateType === "Full" && entries.filter((e) => e.id_tag.trim()).length === 0 &&
              " (Full + empty = clear the device's list)"}
          </span>
          <button type="button" className="maint-btn-small" onClick={add}>
            <Plus size={12} /> Add entry
          </button>
        </div>
      </div>

      <div className="rcmd-form-actions">
        {entry.destructive && entry.confirm_text && (
          <input className="rcmd-confirm" value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={`Type "${entry.confirm_text}" to confirm`} />
        )}
        <button type="submit" className="maint-btn-primary" disabled={submitting || !confirmOk}>
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
};

export default SendLocalListForm;
